import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Grüezi! Ich bin der MBW Swiss KI-Assistent. Wie kann ich Ihnen heute bei Fragen zu Firmengründung, Steuern oder Treuhand helfen?",
    },
  ]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Entschuldigung, der KI-Dienst ist momentan nicht erreichbar. Bitte kontaktieren Sie uns direkt unter office@mbw-swiss.ch." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-[72px] right-6 md:bottom-6 md:right-[210px] z-[10000] w-14 h-14 rounded-full bg-[#991B1B] hover:bg-[#7F1D1D] shadow-lg flex items-center justify-center text-[#1A2433] transition-all hover:scale-105"
        data-testid="chat-widget-toggle"
        aria-label="KI-Assistent öffnen"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-[10000] w-[calc(100vw-3rem)] max-w-md h-[70vh] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden mbw-slide-in"
          data-testid="chat-widget-window"
        >
          <div className="bg-[#1A2433] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#991B1B] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#1A2433]" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-semibold leading-tight">MBW KI-Assistent</div>
              <div className="text-xs text-slate-300">Schweizer Steuern & Firmengründung</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-300 hover:text-white" data-testid="chat-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 chat-scroll bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#1A2433] text-white rounded-br-sm"
                      : "bg-white text-slate-700 border border-slate-200 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-slate-200 p-3 bg-white flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Stellen Sie Ihre Frage…"
              className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B] text-sm"
              disabled={busy}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="bg-[#1A2433] hover:bg-[#253447] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 transition-colors"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
